import { Component, EventEmitter, forwardRef, Input, OnInit, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { PrimeIcons } from 'primeng/api';
import { AutoCompleteSelectEvent } from 'primeng/autocomplete';

import { BaseLocationFragment, LocationCreateBase } from '../../generated/graphql.generated';
import { FreyaCommonModule } from '../freya-common/freya-common.module';
import { formatLocationToDisplay } from '../jobs/jobs.util';
import { GoogleHelperService } from '../services/google-helper.service';

type locationOption = BaseLocationFragment | google.maps.places.AutocompletePrediction;
type selectedLocation = BaseLocationFragment | LocationCreateBase;

type SuggestionItem = {
  label: string;
  value: locationOption;
  icon: PrimeIcons;
  isExistingLocation?: boolean;
};

@Component({
  selector: 'app-address-autocomplete',
  templateUrl: './address-autocomplete.component.html',
  styleUrl: './address-autocomplete.component.scss',
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AddressAutocompleteComponent),
      multi: true,
    },
  ],
  imports: [FreyaCommonModule]
})
export class AddressAutocompleteComponent implements OnInit, ControlValueAccessor {
  @Input() existingLocations: BaseLocationFragment[] = [];
  @Input() placeholder: string = '';
  @Input() inputStyle: Record<string, any> = {};
  @Input() ngClass: Record<string, any> = {};

  @Output() addressSelected: EventEmitter<AutoCompleteSelectEvent> = new EventEmitter<AutoCompleteSelectEvent>();
  @Output() addressSearched: EventEmitter<string> = new EventEmitter<string>();

  suggestions: SuggestionItem[] = [];
  selectedLocation: locationOption | null = null;
  formattedExistingLocations: SuggestionItem[] = [];

  private sessionToken: google.maps.places.AutocompleteSessionToken;

  private onChange: (value: selectedLocation | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private googleHelperService: GoogleHelperService) {}

  ngOnInit(): void {
    this.refreshSessionToken();
    this.formattedExistingLocations = this.existingLocations.map((location) => ({
      label: formatLocationToDisplay(location),
      value: location,
      icon: PrimeIcons.MAP,
    }));

  }

  async fetchAutocompleteSuggestions(input: string): Promise<void> {
    if (!input) {
      this.suggestions = [];
      return;
    }

    // First try to fetch locations from the existing locations
    let suggestions: SuggestionItem[] = [];

    if (this.existingLocations.length > 0) {
      suggestions = this.formattedExistingLocations.filter(({ label }) =>
        label.toLowerCase().includes(input.toLowerCase())
      );
    }

    // If there are no suggestions from existing locations, fetch from Google Places API
    if (suggestions.length === 0) {
      const request: google.maps.places.AutocompletionRequest = {
        input,
        locationRestriction: {
          west: -141.0,
          north: 83.0,
          east: -52.0,
          south: 23.0,
        },
        language: 'en-US',
        sessionToken: this.sessionToken,
      };

      const response = await this.googleHelperService.getAddressAutocomplete(request);
      suggestions = response.predictions.map((item) => ({
        label: item.description,
        value: item,
        icon: PrimeIcons.MAP_MARKER,
        isExistingLocation: false,
      }));
    }

    this.suggestions = suggestions;
  }

  async onPlaceSelected(event: {
    originalEvent: Event;
    value: { value: BaseLocationFragment | google.maps.places.AutocompletePrediction };
  }): Promise<void> {
    const selectedValue = event?.value?.value;

    // Handle invalid selection
    if (!selectedValue) {
      return;
    }

    // Type guard for BaseLocationFragment (has 'id')
    if ('id' in selectedValue) {
      this.selectedLocation = selectedValue;
      this.onChange(selectedValue);
      this.addressSelected.emit({
        originalEvent: event.originalEvent,
        value: selectedValue,
      });
      return;
    }

    // Type guard for AutocompletePrediction (has 'place_id')
    if ('place_id' in selectedValue) {
      try {
        const placeDetails = await this.googleHelperService.getGoogleAddress({
          formattedAddress: (selectedValue as google.maps.places.AutocompletePrediction).description,
        });

        const locationNode = this.googleHelperService.convertGoogleLocationToCreateLocationInput(placeDetails);

        this.selectedLocation = selectedValue;

        // Emit the event to the parent
        this.addressSelected.emit({
          originalEvent: event.originalEvent,
          value: locationNode,
        });

        // Update the form control and ngModel
        this.onChange(locationNode);
      } catch (error) {
        console.error('Error fetching place details:', error);
      } finally {
        // Refresh session token after place is selected
        this.refreshSessionToken();
      }
      return;
    }

    // If neither type matches, do nothing
    return;
  }

  private refreshSessionToken(): void {
    this.sessionToken = new google.maps.places.AutocompleteSessionToken();
  }

  // ControlValueAccessor methods
  writeValue(value: BaseLocationFragment | null): void {
    this.selectedLocation = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
}
